import axios from 'axios';
import Cookies from 'js-cookie';
import {
  getContentType,
  triggerCustomEvent,
  parseTemplate,
  featureEnabled,
} from 'lib/helpers';

const videoIcon = `
  <svg width="30" height="30" class="partner-results__video-icon" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M15 2C7.8203 2 2 7.8203 2 15C2 22.1797 7.8203 28 15 28C22.1797 28 28 22.1797 28 15C28 7.8203 22.1797 2 15 2ZM0 15C0 6.71573 6.71573 0 15 0C23.2843 0 30 6.71573 30 15C30 23.2843 23.2843 30 15 30C6.71573 30 0 23.2843 0 15Z" />
    <path d="M19.3661 14.1483L12.5241 9.93791C11.8578 9.52789 11 10.0072 11 10.7896V19.2104C11 19.9928 11.8578 20.4721 12.5241 20.0621L19.3661 15.8517C20.0006 15.4612 20.0006 14.5388 19.3661 14.1483Z" />
  </svg>`;

export default class PartnerImageResults {
  constructor(container, term, serviceUrl, gridCallback = null) {
    this.container = container;

    this.searchTerm = term;
    this.serviceUrl = serviceUrl;

    this.gridContainer = container.querySelector('.ez-resource-grid');

    this.setupResultTemplate();

    window.addEventListener('InitAllGrids', this.recalcPartnerGrid);

    this.gridCallback = gridCallback;
  }

  setupResultTemplate() {
    this.defaultResultTemplate = `
      <li class="ez-resource-grid__item ez-resource-thumb" data-w="{{image_width}}" data-h="{{image_height}}" data-index="{{index}}" data-item-id="{{id}}">
        <a class="ez-resource-thumb__link" title="{{description}}" target="_new" href="{{url}}">
          <img class="ez-resource-thumb__img" src="{{image_src}}" alt="{{description}}" />
        </a>
      </li>
      `;

    // get template element, if a custom one exists
    this.customResultTemplateElem = document.querySelector('[data-partner-images-target="resultTemplate"]');

    if (this.customResultTemplateElem) {
      // save template html to variable
      this.resultTemplate = document.querySelector('[data-partner-images-target="resultTemplate"]').innerHTML;
      // now that we have the custom template html, remove the template element (because IE is stupid)
      this.customResultTemplateElem.parentNode.removeChild(this.customResultTemplateElem);
    } else {
      this.resultTemplate = this.defaultResultTemplate;
    }
  }

  renderResults = (results, options) => {
    if (options.controllerElem.classList.contains('is-hidden')) {
      options.controllerElem.classList.remove('is-hidden');
    }

    const images = results.data;

    images.forEach((image, index, array) => {
      const item = image;
      const itemThumb = options.largeThumb ? 'preview' : 'large_thumb';

      item.index = this.calculateTotalIndex(index, options.page, options.limit);
      item.url = `${options.impactRadiusUrl}?u=${encodeURI(item.url)}`;
      item.image_src = item.assets[itemThumb].url;
      item.image_height = item.assets[itemThumb].height;
      item.image_width = item.assets[itemThumb].width;

      if (options.urlparams) {
        item.url += `&${options.urlparams}`;
      }

      const subId3 = (this.placement === 'similar') ? `${this.mostRecentSearchTerm}_${item.id}_top` : `${this.searchTerm}_${item.id}_${this.placement}`;

      item.url += `&subId3=${subId3}`;

      if ((parseInt(item.image_height, 10) / parseInt(item.image_width, 10)) > 0.45) {
        const itemHTML = parseTemplate(this.resultTemplate, item);

        this.gridContainer.insertAdjacentHTML('beforeend', itemHTML);

        if (getContentType() === 'video') {
          const liItem = this.gridContainer.lastElementChild;
          liItem.insertAdjacentHTML('beforeend', videoIcon);
        }
      }

      // After last image in set, run/re-run the justified grid script
      if (index === array.length - 1) {
        this.gridContainer.classList.remove('is-hidden');

        window.ezResourceGrid({
          gridSelector: this.gridContainer,
          options: {
            rowHeight: options.rowHeight || 70,
            maxRows: parseInt(options.maxRows, 10) || null,
          },
          callback: this.gridCallback,
        });

        // If grey box, we need to let the height adapt to the content
        // after it is loaded in (to minimize CLS)
        if (this.gridContainer.closest('.partner-sponsored-results')) {
          const gridHeight = this.gridContainer.height;

          this.gridContainer.style.height = 'unset';
          this.gridContainer.style.maxHeight = `${gridHeight}px`;
        }
      }
    });
  }

  calculateTotalIndex(index, page, pageLimit) {
    return (pageLimit * (page + 1)) - (pageLimit - index);
  }

  recalcPartnerGrid = () => {
    window.ezResourceGrid({
      gridSelector: this.gridContainer,
      options: {
        rowHeight: this.recalcRowHeight,
        maxRows: this.recalcMaxRows,
      },
    });
  }

  generateUrl = (options) => {
    // Copy the url, don't modify in place
    let url = this.serviceUrl.slice(0);

    if (typeof (this.searchTerm) === 'undefined' || !this.searchTerm) {
      const searchField = document.querySelector('[data-search-form-target="input"]');
      this.searchTerm = searchField.value;
    }

    const contentType = (getContentType() === 'photo') ? 'photography' : getContentType();

    url += `?term=${encodeURI(this.searchTerm)}&locale=${options.loc}&type=${contentType}`;

    if (featureEnabled('partner-service-v2')) {
      url += '&v=2';
    }

    const resultsLimit = typeof options.limit !== 'undefined' ? options.limit : 80;

    url += `&per_page=${resultsLimit}`;

    if (options.page) {
      url += `&page=${(parseInt(options.page, 10) + 1)}`; // api uses 1 based index
    } else {
      url += '&page=1';
    }

    // Intentionally using abstract equality operator
    // to check for null AND undefined
    if (options.padResults != null) {
      url += `&pad_images=${options.padResults}`;
    }

    if (options.apiparams) {
      url += `&${options.apiparams}`;
    }

    if (options.padWithTerm) {
      url += `&pad_with_term=${options.padWithTerm}`;
    }
    return url;
  }

  makeUrlParams(sharedId, subId1, subId2, adType = null, adPlacement = null) {
    return `sharedid=${sharedId}`
          + `&subId1=${subId1}`
          + `&subId2=${subId2}`
          + `&adtype=${adType}`
          + `&adplacement=${adPlacement}`;
  }

  getResults(options) {
    this.placement = options.placement || null;
    this.recalcRowHeight = options.rowHeight || 70;
    this.recalcMaxRows = parseInt(options.maxRows, 10) || null;

    const url = this.generateUrl(options);

    axios.get(url)
      .then((response) => {
        if (response.data) {
          if (response.data.total_count) {
            if (!this.container.hasAttribute('data-total-count')) {
              this.container.setAttribute('data-total-count', response.data.total_count);
            }

            this.renderResults(response.data, options);

            triggerCustomEvent('resultsLoaded');
          } else {
            triggerCustomEvent('resultsLoaded', 'END_OF_RESULTS');
          }
        }
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  get mostRecentSearchTerm() {
    return Cookies.get('most_recent_search') || 'null';
  }
}
